/* eslint-disable react-hooks/exhaustive-deps */
import CloseIcon from '@mui/icons-material/Close'
import { Box, Card, CardContent, IconButton, Stack, Tab, TablePagination, Tabs, Typography } from '@mui/material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { ArrowSwapVertical } from 'iconsax-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import TablePaginationActions from '../../components/GroupsTable/Table/TablePaginationActions'
import { MonitoringEvent } from '../../components/GroupsTable/types'
import Loader from '../../components/Loader'
import { useGetIncidentsLists } from '../../hook/notifications'
import { useSessionContex } from '../../SessionContex'
import { getColorCode } from '../../utils/helper'

dayjs.extend(relativeTime)

interface NotificationListProps {
  open: boolean
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>
}

const NotificationList = ({ open, setOpen }: NotificationListProps) => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalNotification, setTotalNotification] = useState(0)
  const [tab, setTab] = useState(0) // 0: All, 1: Unread
  const navigate = useNavigate()

  const {
    data: notificationLists,
    refetch,
    isLoading,
    isFetching
  } = useGetIncidentsLists({
    limit: rowsPerPage,
    offset: page * rowsPerPage,
    monitoring_type: ''
  })

  const { notificationListReCall, setNotificationListReCall } = useSessionContex()

  // 🔹 Extract data rows safely
  useEffect(() => {
    if (notificationLists?.total) {
      const totalLength = notificationLists.total?.split('/')[1]
      setTotalNotification(Number(totalLength))
    }
  }, [notificationLists])

  const rows: MonitoringEvent[] = useMemo(() => {
    return notificationLists ? notificationLists.data.map(group => ({ ...group })) : []
  }, [notificationLists, page, rowsPerPage])

  console.log('rows', rows)

  useEffect(() => {
    refetch()
  }, [page, rowsPerPage])

  useEffect(() => {
    if (open) refetch()
  }, [open])

  useEffect(() => {
    if (notificationListReCall && open) {
      refetch().finally(() => setNotificationListReCall(false))
    }
  }, [open, notificationListReCall, refetch, setNotificationListReCall])

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setNotificationListReCall(false)
    }
  }, [isLoading, isFetching])

  useEffect(() => {
    setPage(0)
  }, [])

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  return (
    <React.Fragment>
      {/* Tabs for All/Unread */}
      <Tabs
        value={tab}
        onChange={(_, newTab) => setTab(newTab)}
        sx={{
          minHeight: 38,
          marginBottom: 1,
          padding: '0px 10px',
          '& .MuiTabs-indicator': {
            backgroundColor: getColorCode('blueBlue')
          },
          '.Mui-selected': {
            color: `${getColorCode('blueBlue')} !important`,
            path: {
              fill: `${getColorCode('blueBlue')} !important`
            }
          }
        }}
      >
        <Tab
          label='All'
          sx={{
            fontWeight: 600,
            minHeight: 36
          }}
          className='customer-tab'
        />
        <Tab
          label='Unread'
          sx={{
            fontWeight: 600,
            minHeight: 36
          }}
          className='customer-tab'
        />
      </Tabs>

      {/* New label, Sort icon, View All */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          px: 1,
          padding: '0px 10px'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ color: '#000', fontWeight: 600, mr: 1 }}>New</Typography>
          <ArrowSwapVertical size='18' color='#000' />
        </Box>
        <Typography sx={{ color: getColorCode('blueBlue'), fontWeight: 600, cursor: 'pointer', fontSize: 15 }}>
          View All &gt;
        </Typography>
      </Box>

      <Box
        sx={{
          width: '100%',
          maxWidth: 400,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: '77vh',
          overflowY: 'auto',
          padding: '0px 10px'
        }}
      >
        <Stack spacing={2}>
          {isLoading || isFetching || notificationListReCall ? (
            <div className='flex  items-center h-[84vh]'>
              <Loader />
            </div>
          ) : rows?.length === 0 ? (
            <Typography textAlign='center' color='text.secondary'>
              No notifications found.
            </Typography>
          ) : (
            <>
              {rows?.map(item => (
                <Card
                  key={item.id}
                  sx={{
                    borderRadius: 2,
                    position: 'relative',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      transition: 'all 0.2s ease-in-out'
                    }
                  }}
                  className={`active-bg-${process.env.REACT_APP_COLOR} `}
                  onClick={() => {
                    navigate('/notifications', { state: { data: item } })
                    setOpen?.(false)
                  }}
                >
                  {/* Close Icon */}
                  <IconButton
                    size='small'
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 2,
                      color: getColorCode('blueBlue')
                    }}
                    onClick={e => {
                      e.stopPropagation() /* handle remove */
                    }}
                  >
                    <CloseIcon fontSize='small' />
                  </IconButton>
                  <CardContent sx={{ py: 1.5, pb: 0 }} className='!pb-[12px]'>
                    <Typography
                      variant='subtitle1'
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        lineHeight: 1.3
                      }}
                    >
                      {item.message}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.85rem' }
                      }}
                      className='mt-1'
                    >
                      {dayjs(item.timestamp).fromNow()}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </Stack>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <TablePagination
          rowsPerPageOptions={[10, 20, 30, 40, 50, 100, 500]}
          component='div'
          count={totalNotification}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
          labelDisplayedRows={() => ''}
          classes={{ toolbar: '!p-0' }}
        />
      </Box>
    </React.Fragment>
  )
}

export default NotificationList
